<template>
  <div>
    <a-lazy-hydrate when-visible>
      <a-breadcrumbs />
    </a-lazy-hydrate>
    <a-paginated-news-list
      ref="paginatedNewsList"
      page-type="allNews"
      :pagination="pagination"
      :pages-count="pagesCount"
      :categories-strip="categoriesStrip"
      :categories-strip-last-modified="categoriesStripLastModified"
      :most-viewed-articles="mostViewedArticles"
      :most-viewed-articles-last-modified="mostViewedArticlesLastModified"
      :all-articles="articles"
      :in-progress="isRequestInProgress"
      @get-new-chunk="getNextArticleChunk"
    />
    <a-pagination-links-for-google
      :page="pagination.page"
      :pages-count="pagesCount"
      :non-paginated-url="canonicalUrlWithoutPagination"
    />
    <a-url-pagination
      :item-count="articles.length"
      :pagination.sync="pagination"
      :initial-page="initialPage"
      :pages-count="pagesCount"
      :item-selector="$options.consts.ARTICLE_SLOT_SELECTOR"
      :non-paginated-url="canonicalUrlWithoutPagination"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'

import { throwPageNotFoundError } from '@/plugins/error'
import mixins from '@/utils/mixins'
import {
  METHOD_NAME_TO_RUN_ON_LOGOUT,
  METHOD_NAME_TO_RUN_ON_LOGIN
} from '@/utils/mixins/run-method-on-login-logout'
import APaginatedNewsList from 'shared/APaginatedNewsList'
import { ARTICLE_SLOT_CLASS_NAME } from 'shared/APaginatedNewsList/enums'
import { PAGE_SCHEME_TYPE } from 'enums/pageSchemes'
import { MOST_VIEWED_ARTICLES_MODEL } from 'enums/responseModels'
import ABreadcrumbs from 'shared/ABreadcrumbs'
import APaginationLinksForGoogle from 'shared/APaginationLinksForGoogle'
import {
  getPageNumberFromRoute,
  promiseAllWithFallback
} from '@/plugins/helper'
import { applyFallback } from '@/utils/helpers'
import AUrlPagination from 'shared/AUrlPagination/index.vue'
import { PENDING_ACTION, METHOD_NAME } from 'enums/pending-actions'
import {
  generateCanonicalUrlForStaticPage,
  generatePaginatedCanonicalUrl,
  checkIfNonCanonicalPath
} from '@/utils/helpers/canonical-links'
import { redirectHandler } from '@/utils/helpers/redirects/helpers'
import { ROUTE } from 'enums/routes'
import { handleNextChunks } from '@/utils/helpers/handle-duplicates'
import { pick } from 'ramda'

const CATEGORIES_MODEL = [
  {
    Name: '',
    Slug: ''
  }
]

/**
 * How many news should be taken from back-end for all-news page
 * @type {number}
 */
const DEFAULT_TAKE = 25

async function getAllNewsData({ ctx, route, store }) {
  const initialPage = getPageNumberFromRoute(route)

  const pagination = {
    take: DEFAULT_TAKE,
    page: initialPage
  }
  const dispatch = store.dispatch

  const promiseSettings = [
    {
      promise: dispatch('articles/requestAllNews', pagination),
      required: false,
      errorMessage: 'Error has occurred while trying to get All News',
      fallbackData: []
    },
    {
      promise: dispatch('articles/requestAllNewsPinnedCategories'),
      required: false,
      errorMessage: 'Error has occurred while trying to get Pinned Categories',
      fallbackData: []
    },
    {
      promise: dispatch('articles/requestMostViewedArticles', {
        isSponsored: false
      }),
      required: false,
      errorMessage:
        'Error has occurred while trying to get Most Viewed Articles',
      fallbackData: []
    }
  ]

  const promiseAllResult = await promiseAllWithFallback(promiseSettings, ctx)

  if (promiseAllResult.every(({ error }) => error)) {
    throw new Error()
  }

  const [
    {
      data: { Articles, PagesCount = 0 }
    },
    {
      data: { categoriesStrip, categoriesStripLastModified }
    },
    {
      data: { mostViewedArticles, mostViewedArticlesLastModified }
    }
  ] = promiseAllResult

  if (PagesCount && initialPage > PagesCount) {
    return throwPageNotFoundError(ctx, 'AllNewsPage')
  }

  return {
    articles: Articles,
    pagesCount: PagesCount,
    initialPage,
    pagination,
    categoriesStrip: applyFallback(categoriesStrip, CATEGORIES_MODEL),
    categoriesStripLastModified,
    mostViewedArticles: applyFallback(
      mostViewedArticles,
      MOST_VIEWED_ARTICLES_MODEL
    ),
    mostViewedArticlesLastModified
  }
}

function generateDefaultAllNewsContentDataModel() {
  return {
    articles: [],
    mostViewedArticles: [],
    categoriesStrip: []
  }
}

export default {
  name: 'AllNewsPage',
  scrollToTop: true,
  components: {
    APaginatedNewsList,
    ABreadcrumbs,
    APaginationLinksForGoogle,
    AUrlPagination
  },
  mixins: [
    mixins.setPageLoaded,
    mixins.refreshDataHandler,
    mixins.runMethodOnLoginLogout,
    mixins.refreshDataListenersFactory({
      refreshHandlerByType: {
        [PENDING_ACTION.SAVE_ARTICLE]: METHOD_NAME.UPDATE_IS_SAVED_VALUE,
        [PENDING_ACTION.FOLLOW_TAG]: METHOD_NAME.UPDATE_IS_FOLLOWED_TAG_VALUE
      }
    }),
    mixins.delayedContentRefresh
  ],
  head() {
    return this.$generateMetaTags({
      titleParam: 'All news',
      descriptionParam: 'All news',
      schemes: [
        { type: PAGE_SCHEME_TYPE.WEB_SITE },
        { type: PAGE_SCHEME_TYPE.ORGANIZATION }
      ],
      canonicalUrl: this.canonicalUrl
    })
  },
  async asyncData(ctx) {
    const { path: currentPath, fullPath } = ctx.route

    try {
      const canonicalUrlWithoutPagination = generateCanonicalUrlForStaticPage(
        ctx,
        {
          staticPath: ROUTE.ALL_NEWS
        }
      )
      const canonicalUrl = generatePaginatedCanonicalUrl({
        canonicalUrlWithoutPagination,
        path: currentPath
      })

      if (checkIfNonCanonicalPath(ctx, { currentPath, canonicalUrl })) {
        redirectHandler({ ctx, to: canonicalUrl })
        return
      }

      const allNewsData = await getAllNewsData({
        ctx,
        route: ctx.route,
        store: ctx.store
      })

      return {
        canonicalUrl,
        canonicalUrlWithoutPagination,
        ...allNewsData
      }
    } catch (err) {
      ctx.app.$errorHandler(err, ctx, {
        showErrorPage: true,
        showMessage: false,
        componentName: 'AllNews page'
      })
    } finally {
      await ctx.store.dispatch('router/removeRouteFromQueue', fullPath)
    }
  },
  consts: {
    ARTICLE_SLOT_SELECTOR: `.${ARTICLE_SLOT_CLASS_NAME}`
  },
  data() {
    const initialPage = getPageNumberFromRoute(this.$route)

    return {
      ...generateDefaultAllNewsContentDataModel(),
      pagesCount: 0,
      initialPage,
      pagination: {
        take: DEFAULT_TAKE,
        page: initialPage
      },
      canonicalUrl: this.$route.path,
      canonicalUrlWithoutPagination: this.$route.path,
      categoriesStripLastModified: null,
      mostViewedArticlesLastModified: null,
      inProgress: false,
      shift: 0,
      naming: 'capitalize-each-word'
    }
  },
  computed: {
    isRequestInProgress() {
      return this.inProgress || this.isPendingContentRefresh
    }
  },
  methods: {
    ...mapActions({
      requestNewArticles: 'articles/requestAllNews'
    }),
    async getArticles(pagination) {
      try {
        const { Articles, PagesCount } = await this.requestNewArticles(
          pagination
        )
        this.pagesCount = PagesCount

        return Articles
      } catch (err) {
        this.$errorHandler(err, this, {
          showErrorPage: true,
          showMessage: false
        })
      }
    },
    getNextArticleChunk() {
      this.pagination.page++
      handleNextChunks({
        shift: this.shift,
        pagination: this.pagination,
        pagesCount: this.pagesCount,
        setInProgressTrueFn: () => {
          this.inProgress = true
        },
        setInProgressFalseFn: () => {
          this.inProgress = false
        },
        setShiftFn: newVal => {
          this.shift = newVal
        },
        requestNextChunkFn: this.getArticles.bind(this),
        addNewArticlesFn: async newArticles => {
          this.articles = [...this.articles, ...newArticles]
          await this.$nextTick()
        },
        getExistingArticleIdsFn: () => this.articles.map(({ id }) => id)
      })
    },
    [METHOD_NAME.UPDATE_IS_SAVED_VALUE](payload) {
      this.articles = this.$_refreshDataHandler_updateIsSavedValue(payload)
    },
    [METHOD_NAME.UPDATE_IS_FOLLOWED_TAG_VALUE](payload) {
      this.articles = this.$_refreshDataHandler_updateIsFollowedTagValue(
        payload
      )
    },
    [METHOD_NAME_TO_RUN_ON_LOGOUT]() {
      this.articles = this.$_refreshDataHandler_resetActionIconValues()
    },
    async [METHOD_NAME_TO_RUN_ON_LOGIN]() {
      const {
        Article,
        Tag
      } = await this.$_refreshDataHandler_getSavedIdsOfEntities([
        this.$_refreshDataHandler_savedEntity.ARTICLE,
        this.$_refreshDataHandler_savedEntity.TAG
      ])
      this.articles = this.$_refreshDataHandler_updateArticleSavedEntities({
        articleIds: Article,
        tagIds: Tag
      })
    }
  },
  mounted() {
    const dataKeysForDelayedContentRefresh = Object.keys(
      generateDefaultAllNewsContentDataModel()
    )

    this.$_delayedContentRefresh_run({
      getOldDataFn: () => pick(dataKeysForDelayedContentRefresh, this),
      requestNewDataFn: async () => {
        const newData = await getAllNewsData({
          ctx: this,
          route: this.$route,
          store: this.$store
        })

        return pick(dataKeysForDelayedContentRefresh, newData)
      },
      dataComparisonKeysToOmitDeep: ['lastModified'],
      dataChangedCallbackFn: newData => {
        Object.assign(this, pick(dataKeysForDelayedContentRefresh, newData))
        this.$refs.paginatedNewsList.rerenderNonHydratedComponents()
      }
    })
  }
}
</script>
